import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const heroSlides = [
    {
      id: 1,
      title: "Authentic Mediterranean Flavors",
      subtitle: "Grilled to Perfection, Served with Love",
      description: "Experience the rich traditions of Mediterranean cuisine with our flame-grilled kebabs, fresh ingredients, and time-honored recipes passed down through generations.",
      image: "https://images.pexels.com/photos/5474640/pexels-photo-5474640.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop",
      cta: "Order Fresh Now"
    },
    {
      id: 2,
      title: "Family Recipes Since 1987",
      subtitle: "Where Every Meal Tells a Story",
      description: "Discover the authentic taste of the Mediterranean with our traditional cooking methods, premium ingredients, and the warmth of family hospitality.",
      image: "https://images.pexels.com/photos/4253302/pexels-photo-4253302.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop",
      cta: "Explore Our Story"
    },
    {
      id: 3,
      title: "Fresh Ingredients Daily",
      subtitle: "From Our Kitchen to Your Table",
      description: "We source the finest ingredients daily to ensure every bite delivers the authentic Mediterranean experience you deserve.",
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop",
      cta: "View Menu"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <section className="relative h-screen overflow-hidden bg-text-primary">
      {/* Hero Slides */}
      <div className="relative h-full">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Background Image */}
            <div className="absolute inset-0">
              <Image
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-text-primary/80 via-text-primary/60 to-text-primary/40"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 h-full flex items-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="max-w-3xl">
                  <h1 className="font-headline text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                    {slide.title}
                  </h1>
                  <h2 className="font-accent text-xl sm:text-2xl lg:text-3xl text-accent-300 mb-6">
                    {slide.subtitle}
                  </h2>
                  <p className="font-body text-lg sm:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl">
                    {slide.description}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      to="/order-online-streamlined-experience"
                      className="btn-conversion text-center animate-pulse-subtle"
                    >
                      <Icon name="ShoppingBag" size={20} className="mr-2" />
                      {slide.cta}
                    </Link>
                    <Link
                      to="/interactive-menu-culinary-discovery"
                      className="px-8 py-4 border-2 border-white text-white font-cta font-semibold rounded-lg hover:bg-white hover:text-text-primary transition-organic text-center"
                    >
                      <Icon name="ChefHat" size={20} className="mr-2" />
                      Explore Menu
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Controls */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-organic ${
                index === currentSlide ? 'bg-accent' : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Arrow Controls */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 p-3 rounded-full bg-white/20 hover:bg-white/30 text-white transition-organic backdrop-blur-sm"
        aria-label="Previous slide"
      >
        <Icon name="ChevronLeft" size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 p-3 rounded-full bg-white/20 hover:bg-white/30 text-white transition-organic backdrop-blur-sm"
        aria-label="Next slide"
      >
        <Icon name="ChevronRight" size={24} />
      </button>

      {/* Trust Indicators */}
      <div className="absolute bottom-8 right-8 z-20 hidden lg:flex items-center space-x-6 text-white">
        <div className="flex items-center space-x-2">
          <Icon name="Star" size={20} className="text-accent fill-current" />
          <span className="font-semibold">4.8/5 Rating</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={20} className="text-trust-300" />
          <span>15-20 min delivery</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={20} className="text-success-300" />
          <span>Fresh guarantee</span>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;